import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mic, Square, Settings, MessageCircle, User, Bot, Waves, Volume2 } from 'lucide-react';
import { nativeRealtimeAudioManager } from '../services/nativeRealtimeAudio';
import { getAvailableVoices } from '../services/audioApi';

const NativeRealtimeOverlay = ({ 
  onClose,
  selectedVoice = 'alloy',
  onVoiceChange,
  existingConversation = [],
  selectedModel = 'gpt-4o-realtime-preview-2024-12-17'
}) => {
  const [status, setStatus] = useState('idle');
  const [conversation, setConversation] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [error, setError] = useState(null);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isHandsFree, setIsHandsFree] = useState(true); // Default to hands-free mode
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);

  // Initialize conversation with existing chat history
  useEffect(() => {
    if (existingConversation && existingConversation.length > 0) {
      const convertedHistory = existingConversation.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp || Date.now()).getTime(),
        audio: null,
        isFromHistory: true
      }));
      
      setConversation(convertedHistory);
      nativeRealtimeAudioManager.setInitialConversation(convertedHistory);
    }
  }, [existingConversation]);

  // Load available voices only (don't initialize connection)
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const { voices } = await getAvailableVoices();
        setAvailableVoices(voices);
      } catch (error) {
        console.error('Failed to load voices:', error);
      }
    };
    loadVoices();

    return () => {
      // Cleanup only if initialized
      if (isConnected) {
        nativeRealtimeAudioManager.cleanup();
      }
    };
  }, []);

  // Update voice when it changes
  useEffect(() => {
    nativeRealtimeAudioManager.setVoice(selectedVoice);
  }, [selectedVoice]);

  const handleNewMessage = useCallback((message) => {
    setConversation(prev => {
      const newConversation = [...prev];
      const lastIndex = newConversation.length - 1;
      
      if (lastIndex >= 0 && newConversation[lastIndex].role === message.role && newConversation[lastIndex].isRealtime) {
        // Update existing streaming message
        newConversation[lastIndex] = message;
      } else {
        // Add new message
        newConversation.push(message);
      }
      
      return newConversation;
    });
  }, []);

  const handleError = useCallback((error) => {
    setError(error.message || 'An error occurred');
    setStatus('error');
    setIsConnected(false);
  }, []);

  const handleStatusChange = useCallback((newStatus) => {
    setStatus(newStatus);
    setIsRecording(newStatus === 'recording');
    setIsUserSpeaking(newStatus === 'listening' || newStatus === 'recording');
    
    // Update connection status
    if (newStatus === 'ready') {
      setIsConnected(true);
    }
  }, []);

  const handleAudioStart = useCallback(() => {
    setIsAISpeaking(true);
  }, []);

  const handleAudioEnd = useCallback(() => {
    setIsAISpeaking(false);
  }, []);

  const handleInitialize = async () => {
    if (isConnected) return;
    
    try {
      setStatus('initializing');
      setError(null);
      
      nativeRealtimeAudioManager.setModel(selectedModel);
      nativeRealtimeAudioManager.setVoice(selectedVoice);
      
      const success = await nativeRealtimeAudioManager.initialize(
        handleNewMessage,
        handleError,
        handleStatusChange,
        handleAudioStart,
        handleAudioEnd
      );

      if (success) {
        setStatus('ready');
        setIsConnected(true);
        
        if (isHandsFree) {
          // Start continuous recording for hands-free mode
          await nativeRealtimeAudioManager.startContinuousRecording();
        }
      } else {
        setError('Failed to initialize native realtime audio conversation');
        setStatus('error');
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleStartRecording = async () => {
    if (!isHandsFree) {
      // Push-to-talk mode
      if (!isConnected) {
        await handleInitialize();
      } else {
        await nativeRealtimeAudioManager.startRecording();
      }
    }
  };

  const handleStopRecording = () => {
    if (!isHandsFree) {
      nativeRealtimeAudioManager.stopRecording();
    }
  };

  const toggleMode = async () => {
    const newHandsFree = !isHandsFree;
    setIsHandsFree(newHandsFree);
    
    if (isConnected) {
      if (newHandsFree) {
        await nativeRealtimeAudioManager.startContinuousRecording();
      } else {
        nativeRealtimeAudioManager.stopContinuousRecording();
      }
    }
  };

  const handleClearConversation = () => {
    nativeRealtimeAudioManager.clearConversation();
    setConversation([]);
  };

  const getStatusMessage = () => {
    if (isUserSpeaking) return 'Listening...';
    if (isAISpeaking) return 'Speaking...';
    if (status === 'initializing') return 'Connecting...';
    if (status === 'error') return error || 'Connection error';
    if (isConnected) return isHandsFree ? 'Hands-free conversation' : 'Push to talk';
    return 'Ready to connect';
  };

  const getStatusColor = () => {
    if (isUserSpeaking) return 'text-blue-600';
    if (isAISpeaking) return 'text-green-600';
    if (status === 'error') return 'text-red-600';
    if (isConnected) return 'text-green-600';
    return 'text-gray-600';
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg">
              <Waves className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">OpenAI Realtime API</h2>
              <p className="text-sm text-gray-600">
                Native real-time voice chat with {selectedModel}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Connection indicator */}
            <div className={`w-3 h-3 rounded-full ${
              isConnected ? 'bg-green-500' : 'bg-red-500'
            }`} title={isConnected ? 'Connected' : 'Disconnected'} />
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="p-4 bg-gray-50 border-b border-gray-200 space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 min-w-16">Voice:</label>
              <select
                value={selectedVoice}
                onChange={(e) => onVoiceChange?.(e.target.value)}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {availableVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} - {voice.description}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="text-xs text-gray-600">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <strong>Features:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• Server-side Voice Activity Detection</li>
                    <li>• True real-time audio streaming</li>
                    <li>• Native OpenAI integration</li>
                  </ul>
                </div>
                <div>
                  <strong>Status:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• Connection: {isConnected ? '✅ Active' : '❌ Inactive'}</li>
                    <li>• Recording: {isRecording ? '🎤 Active' : '⏸️ Stopped'}</li>
                    <li>• AI Speaking: {isAISpeaking ? '🔊 Active' : '🔇 Silent'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Bar */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                status === 'ready' ? 'bg-green-500' :
                status === 'recording' || status === 'listening' ? 'bg-blue-500 animate-pulse' :
                status === 'error' ? 'bg-red-500' :
                'bg-yellow-500 animate-pulse'
              }`} />
              <span className={`text-sm font-medium ${getStatusColor()}`}>
                {getStatusMessage()}
              </span>
            </div>
          </div>
        </div>

        {/* Main Conversation Area */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-6">
            
            {/* Visual Status Indicator */}
            <div className="relative">
              <div className={`w-32 h-32 rounded-full border-4 transition-all duration-300 flex items-center justify-center ${
                isRecording 
                  ? 'border-blue-500 bg-blue-50 animate-pulse' 
                  : isAISpeaking
                  ? 'border-green-500 bg-green-50 animate-pulse'
                  : status === 'ready' && isConnected
                  ? 'border-green-500 bg-green-50'
                  : status === 'idle'
                  ? 'border-blue-300 bg-blue-50'
                  : 'border-gray-300 bg-gray-50'
              }`}>
                {isRecording ? (
                  <div className="flex items-center">
                    <Mic className="w-8 h-8 text-blue-600" />
                    <div className="flex gap-1 ml-2">
                      {[...Array(3)].map((_, i) => (
                        <div
                          key={i}
                          className="w-1 h-8 bg-blue-500 rounded-full animate-bounce"
                          style={{ animationDelay: `${i * 0.1}s` }}
                        />
                      ))}
                    </div>
                  </div>
                ) : isAISpeaking ? (
                  <div className="flex items-center">
                    <Bot className="w-8 h-8 text-green-600" />
                    <div className="flex gap-1 ml-2">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className="w-1 h-6 bg-green-500 rounded-full animate-pulse"
                          style={{ animationDelay: `${i * 0.15}s` }}
                        />
                      ))}
                    </div>
                  </div>
                ) : isConnected ? (
                  <MessageCircle className="w-8 h-8 text-green-500" />
                ) : (
                  <Waves className="w-8 h-8 text-gray-400" />
                )}
              </div>

              {/* Connection rings for active state */}
              {(isRecording || isAISpeaking) && (
                <>
                  <div className={`absolute inset-0 rounded-full border-2 animate-ping opacity-75 ${
                    isRecording ? 'border-blue-300' : 'border-green-300'
                  }`} />
                  <div className={`absolute inset-0 rounded-full border animate-ping opacity-50 ${
                    isRecording ? 'border-blue-200' : 'border-green-200'
                  }`} style={{ animationDelay: '0.5s' }} />
                </>
              )}
            </div>

            {/* Status Text */}
            <div>
              <p className={`text-lg font-medium ${getStatusColor()}`}>
                {getStatusMessage()}
              </p>
              {status === 'ready' && isConnected && (
                <p className="text-gray-500 mt-1">
                  Press and hold to speak with native real-time AI
                </p>
              )}
            </div>

            {/* Conversation Count */}
            <div className="text-sm text-gray-600 space-y-1">
              {conversation.length > 0 && (
                <>
                  <p>{conversation.filter(m => !m.isFromHistory).length} real-time exchanges</p>
                  {existingConversation?.length > 0 && (
                    <p className="text-xs">+ {existingConversation.length} from chat history</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="p-6 border-t border-gray-200 bg-white">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={isHandsFree ? handleInitialize : undefined}
              onMouseDown={!isHandsFree ? handleStartRecording : undefined}
              onMouseUp={!isHandsFree ? handleStopRecording : undefined}
              onMouseLeave={!isHandsFree ? handleStopRecording : undefined}
              disabled={status === 'error' || (isHandsFree && isConnected)}
              className={`p-4 rounded-full transition-all duration-200 ${
                isUserSpeaking
                  ? 'bg-blue-500 text-white animate-pulse'
                  : isAISpeaking
                  ? 'bg-green-500 text-white animate-pulse'
                  : isConnected && isHandsFree
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : status === 'ready' && isConnected && !isHandsFree
                  ? 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white hover:scale-105'
                  : !isConnected
                  ? 'bg-gradient-to-r from-blue-400 to-purple-500 hover:from-blue-500 hover:to-purple-600 text-white hover:scale-105'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              } ${status === 'error' ? 'opacity-50' : ''}`}
              title={
                isHandsFree ? 
                  (isConnected ? 'Hands-free active' : 'Start hands-free conversation') :
                  (isUserSpeaking ? 'Release to stop' : 'Hold to speak')
              }
            >
              {isUserSpeaking ? (
                <Square className="w-6 h-6" />
              ) : (
                <Mic className="w-6 h-6" />
              )}
            </button>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                {isHandsFree ? 
                  (isConnected ? 'Hands-free conversation active' : 'Click to start hands-free mode') :
                  (isUserSpeaking ? 'Release to send' : isConnected ? 'Hold to speak' : 'Click to connect')
                }
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Native OpenAI Realtime API • {isHandsFree ? 'Hands-free' : 'Push-to-talk'}
              </p>
            </div>

            <button
              onClick={toggleMode}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              title="Toggle conversation mode"
            >
              {isHandsFree ? '👋 Push-to-talk' : '🎤 Hands-free'}
            </button>

            <button
              onClick={() => nativeRealtimeAudioManager.triggerResponse()}
              disabled={status !== 'ready' || !isConnected}
              className="px-3 py-2 text-sm bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg transition-colors"
              title="Manually trigger AI response"
            >
              Get Response
            </button>

            {conversation.length > 0 && (
              <button
                onClick={handleClearConversation}
                className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Clear conversation"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NativeRealtimeOverlay;